import React from 'react';
import { cn } from '~/lib/utils';
import { ComposerButton } from './ComposerButton';
import styles from './ComposerToolbar.module.css';
import { ModelPill } from './ModelPill';
import type { ModelPillProps } from './ModelPill';
import type { ChatTheme, SendState } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/**
 * The composer's bottom action bar: add on the left, the model it will run on, then everything
 * that commits the message pushed to the trailing edge.
 *
 * Thirty-odd artboards draw this row and every one of them draws the same five boxes in the same
 * order. It is a pure layout molecule — it owns no state, paints no ground of its own, and holds
 * nothing but atoms that were built and audited before it. The only judgement it makes is how wide
 * the trailing gap is, and that is annotated below as the inference it is.
 */
export interface ComposerToolbarProps {
  /**
   * The model pill's own props, minus the theme.
   *
   * `theme` is stripped deliberately: the toolbar already themes its subtree through
   * `chatThemeClass`, and a second theme arriving through this object could contradict it. One
   * bar, one theme decision.
   */
  model: Omit<ModelPillProps, 'theme'>;
  onAdd?: () => void;
  /** The add menu is open beneath the `+`. Paints the button's wash and holds it. */
  addOpen?: boolean;
  /** Accessible name for the icon-only `+`. */
  addLabel?: string;
  /**
   * Modes that are currently on — `<ModePill mode="search" label="Search" />` in `Chat/Search on`,
   * the one artboard that draws one. They sit inside the trailing group, before the count and the send
   * button, because a mode is a property of the message about to leave, not of the composer.
   */
  modes?: React.ReactNode;
  /**
   * Aggregate queue count. Rendered as `{n} queued` — stated once for the whole queue rather than
   * per bubble, which is the decision the four queue artboards make.
   *
   * Its presence also widens the trailing gap; see the stylesheet.
   */
  queueCount?: number;
  /**
   * The commit affordance. `idle` is the tinted hole, `active` the one solid object in the bar,
   * `stop` the square shown while a run is in flight.
   */
  send: { state: SendState; onClick?: () => void; label?: string };
  /**
   * The composer is disabled (`Chat/Disabled`). Forwarded to the two `ComposerButton`s
   * this row renders — the `+` and whichever of send/stop is up — which takes the pointer, the
   * press response and, crucially, the **tab stop** away from each of them.
   *
   * It paints nothing. That artboard's caption is the whole spec — "whole bar at 45%" — and that opacity
   * belongs to the card; every child stays painted exactly as at rest. That is why this prop adds
   * no class and why the stylesheet has no `.disabled` rule to go with it.
   *
   * Deliberately incomplete, and the rest is not this file's to close: `ModelPill` and `ModePill`
   * have no `disabled` prop, so there is nothing to forward and both stay focusable. `modes` is a
   * `ReactNode` slot besides, so even a `ModePill` that had one could not be reached from here.
   * Until those two gain the prop, a disabled `Composer` still needs the `inert` on its card.
   */
  disabled?: boolean;
  theme?: ChatTheme;
  className?: string;
}

/**
 * Accessible names for the two icon-only controls.
 *
 * These strings are not in the Paper file — the artboards draw glyphs, not labels — so they are
 * written here rather than inferred from a number. `stop` gets its own name because the slot's
 * meaning inverts between the two send states and an `aria-label` that still said "Send" while the
 * button stops a run would be worse than no label at all.
 */
const DEFAULT_ADD_LABEL = 'Add attachment or tool';
const SEND_LABEL: Record<SendState, string> = {
  idle: 'Send message',
  active: 'Send message',
  stop: 'Stop generating',
};

export function ComposerToolbar({
  model,
  onAdd,
  addOpen = false,
  addLabel = DEFAULT_ADD_LABEL,
  modes,
  queueCount,
  send,
  disabled = false,
  theme: themeProp,
  className,
}: ComposerToolbarProps) {
  const theme = useV15Theme(themeProp);
  // A count of 0 is not "no queue" — it is a queue that just drained — but the design never draws
  // "0 queued", so the string is suppressed and only a positive count renders. The gap follows the
  // string, not the prop, which is why both read the same expression.
  const hasCount = typeof queueCount === 'number' && queueCount > 0;

  return (
    <div
      className={cn(chatThemeClass(theme), styles.root, className)}
      // Not `role="toolbar"`: that role promises arrow-key roving between the controls, and this
      // bar is plain tab order in every artboard. Claiming it without implementing it would be a
      // worse lie than claiming nothing.
      //
      // And no `aria-disabled` when `disabled` is set, for the same family of reason: this div has
      // no role, and `aria-disabled` on a generic element is mapped by nothing — it would read as
      // a note to ourselves rather than an announcement. The real `disabled` attributes on the
      // buttons below are what a screen reader actually hears.
    >
      <ComposerButton
        action="add"
        open={addOpen}
        onClick={onAdd}
        label={addLabel}
        disabled={disabled}
        theme={theme}
      />
      {/* A literal 4px-wide element, not a gap — chat.md 0.4 draws it as its own div, and the
          toolbar has no row gap for it to be folded into. See the stylesheet. */}
      <div className={styles.spacer} aria-hidden="true" />
      <ModelPill {...model} theme={theme} />
      {/* The one element in the bar allowed to give up width. */}
      <div className={styles.grow} aria-hidden="true" />
      <div className={cn(styles.trailing, hasCount && styles.trailingWide)}>
        {modes}
        {hasCount ? (
          // Painted only. The announcement is made by the always-mounted region below, and a
          // second copy of the same words in the tree would be read twice in browse mode.
          <span className={styles.count} aria-hidden="true">
            {queueCount} queued
          </span>
        ) : null}
        {send.state === 'stop' ? (
          <ComposerButton
            action="stop"
            onClick={send.onClick}
            label={send.label ?? SEND_LABEL.stop}
            disabled={disabled}
            theme={theme}
          />
        ) : (
          <ComposerButton
            action="send"
            active={send.state === 'active'}
            onClick={send.onClick}
            label={send.label ?? SEND_LABEL[send.state]}
            disabled={disabled}
            theme={theme}
          />
        )}
      </div>
      {/* The queue's status, said once, out loud.
       *
       * Mounted unconditionally and emptied rather than removed: a live region only announces
       * changes that happen to a node already in the accessibility tree, so a span that arrives
       * carrying its own first words — the 0 → 1 step, which is exactly the moment queue.md's
       * `Queue drains` board describes ("the run finished, the first queued message became a real
       * turn") and the user is not looking at the bar — is silently dropped by NVDA, JAWS and VoiceOver. Only 1 →
       * 2 → 3 would have announced.
       * It sits outside `.trailing` because that group's 4px/10px gap counts every child: an
       * always-present span in there would open a phantom gap beside send in the states that
       * have no count at all. */}
      <span className={styles.announcer} aria-live="polite">
        {hasCount ? `${queueCount} queued` : ''}
      </span>
    </div>
  );
}
